(() => {
  const API_URL = 'https://api.geoapify.com/v1/geocode/autocomplete';
  const MIN_QUERY_LENGTH = 4;
  const DEBOUNCE_MS = 320;
  const ATTRIBUTION_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'];
  const ATTRIBUTION_KEY = 'supplydoc:equipment-attribution';

  function publishAnalyticsEvent(name, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...detail });
    } catch (error) {
      console.warn(`${name} dataLayer publication failed.`);
    }

    try {
      if (typeof window.Shopify?.analytics?.publish === 'function') {
        const publication = window.Shopify.analytics.publish(name, detail);
        if (publication && typeof publication.catch === 'function') {
          publication.catch(() => console.warn(`${name} Shopify publication failed.`));
        }
      }
    } catch (error) {
      console.warn(`${name} Shopify publication failed.`);
    }
  }

  function initializeAttribution(form) {
    let attribution = {};
    try {
      attribution = JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_KEY) || '{}');
    } catch {
      attribution = {};
    }

    const search = new URLSearchParams(window.location.search);
    ATTRIBUTION_FIELDS.forEach((field) => {
      const value = search.get(field);
      if (value) attribution[field] = value.slice(0, 500);
    });
    attribution.referring_url = attribution.referring_url || document.referrer;

    try {
      window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    } catch {
      // Attribution remains available for this page view when storage is blocked.
    }

    Object.entries(attribution).forEach(([name, value]) => {
      const field = form.elements.namedItem(name);
      if (field) field.value = value;
    });
  }

  function initializeExperiment(section, form) {
    if (section.dataset.experimentEnabled !== 'true') return;
    const experimentId = section.dataset.experimentId.trim();
    if (!experimentId) return;

    const storageKey = `supplydoc:experiment:${experimentId}`;
    const override = new URLSearchParams(window.location.search).get('ab_variant')?.toUpperCase();
    let variant = override === 'A' || override === 'B' ? override : '';
    if (!variant) {
      try { variant = window.localStorage.getItem(storageKey) || ''; } catch {}
    }
    if (variant !== 'A' && variant !== 'B') {
      const allocation = Math.max(0, Math.min(100, Number(section.dataset.experimentBAllocation) || 50));
      variant = Math.random() * 100 < allocation ? 'B' : 'A';
    }
    try { window.localStorage.setItem(storageKey, variant); } catch {}

    form.elements.namedItem('experiment_id').value = experimentId;
    form.elements.namedItem('experiment_variant').value = variant;
    section.dataset.experimentVariant = variant;

    section.querySelectorAll('[data-experiment-hide-on-minimal]').forEach((container) => {
      const hidden = variant === 'B';
      container.hidden = hidden;
      container.querySelectorAll('input, select, textarea').forEach((control) => {
        control.disabled = hidden;
      });
    });

    if (section.dataset.conversionTracking === 'true') {
      publishAnalyticsEvent('experiment_viewed', {
        experiment_id: experimentId,
        experiment_variant: variant,
        page_path: window.location.pathname.replace(/\/$/, '')
      });
    }
  }

  function publishEquipmentLead(form, result, formType) {
    const campaigns = {
      '/pages/section-179-dental-equipment-financing': 'section_179',
      '/pages/certified-pre-owned-vatech-imaging': 'vatech_pre_owned'
    };
    const pagePath = window.location.pathname.replace(/\/$/, '');
    const campaign = campaigns[pagePath];
    if (!campaign || formType !== 'financing_inquiry') return;

    if (form.closest('[data-equipment-repair-section]')?.dataset.conversionTracking !== 'true' || result?.is_test === true) return;

    const detail = {
      form_type: 'financing_inquiry',
      campaign_name: campaign,
      page_path: pagePath,
      lead_status: 'accepted',
      experiment_id: form.elements.namedItem('experiment_id')?.value || '',
      experiment_variant: form.elements.namedItem('experiment_variant')?.value || '',
      ...(result?.lead_id || result?.id ? { lead_id: String(result.lead_id || result.id) } : {})
    };
    publishAnalyticsEvent('campaign_lead_submitted', detail);
  }

  function debounce(callback, wait) {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  }

  function setStatus(statusElement, message, type) {
    statusElement.textContent = message;
    statusElement.classList.remove('is-success', 'is-error');
    if (type) statusElement.classList.add(`is-${type}`);
    if (message) statusElement.focus({ preventScroll: true });
  }

  function updatePhoneValidity(control) {
    if (!control) return;
    const value = String(control.value || '').trim();
    control.setCustomValidity('');

    if (!value) return;
    const digits = value.replace(/\D/gu, '');
    const validDigitCount = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
    const usesValidCharacters = /^[0-9()+.\-\s]+$/u.test(value);

    if (!validDigitCount || !usesValidCharacters) {
      control.setCustomValidity('Enter a 10-digit U.S. phone number, such as (555) 555-0123.');
    }
  }

  function initAddressAutocomplete(section) {
    const apiKey = section.dataset.geoapifyKey;
    const input = section.querySelector('[data-address-input]');
    const suggestions = section.querySelector('[data-address-suggestions]');
    const note = section.querySelector('[data-address-note]');
    const form = section.querySelector('[data-equipment-repair-form]');

    if (!apiKey || !input || !suggestions || !form) return;

    const fields = {
      address: form.querySelector('[data-address-value]'),
      city: form.querySelector('[data-address-city]'),
      state: form.querySelector('[data-address-state]'),
      postcode: form.querySelector('[data-address-postcode]'),
      country: form.querySelector('[data-address-country]'),
      latitude: form.querySelector('[data-address-latitude]'),
      longitude: form.querySelector('[data-address-longitude]')
    };

    let controller;
    let activeIndex = -1;
    let selectedAddress = '';

    function closeSuggestions() {
      suggestions.hidden = true;
      suggestions.innerHTML = '';
      activeIndex = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }

    function clearStructuredAddress() {
      Object.values(fields).forEach((field) => {
        if (field) field.value = '';
      });
      selectedAddress = '';
    }

    function selectResult(result) {
      fields.address.value = result.formatted || input.value;
      fields.city.value = result.city || result.county || '';
      fields.state.value = result.state_code || result.state || '';
      fields.postcode.value = result.postcode || '';
      fields.country.value = result.country || '';
      fields.latitude.value = result.lat || '';
      fields.longitude.value = result.lon || '';
      selectedAddress = fields.address.value;
      input.value = selectedAddress;
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
      if (note) {
        const postalText = fields.postcode.value ? ` ZIP ${fields.postcode.value} captured.` : '';
        note.textContent = `Address selected.${postalText}`;
      }
      closeSuggestions();
    }

    function useEnteredAddress() {
      const enteredAddress = input.value.trim();
      const zipMatch = enteredAddress.match(/\b[0-9]{5}(?:-[0-9]{4})?\b/u);
      fields.address.value = enteredAddress;
      fields.city.value = '';
      fields.state.value = '';
      fields.postcode.value = zipMatch ? zipMatch[0] : '';
      fields.country.value = 'United States';
      fields.latitude.value = '';
      fields.longitude.value = '';
      selectedAddress = enteredAddress;
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
      if (note) {
        note.textContent = zipMatch
          ? 'Using the service address exactly as entered.'
          : 'Using the address as entered. Include its 5-digit ZIP code before submitting.';
      }
      closeSuggestions();
    }

    function renderSuggestions(items) {
      suggestions.innerHTML = '';
      activeIndex = -1;

      if (!items.length) {
        const empty = document.createElement('p');
        empty.className = 'repair-form__suggestion';
        empty.textContent = 'No matching addresses found. Keep typing or enter the full address manually.';
        suggestions.appendChild(empty);
      } else {
        items.forEach((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'repair-form__suggestion';
          button.id = `${suggestions.id}-option-${index}`;
          button.setAttribute('role', 'option');
          button.setAttribute('aria-selected', 'false');
          button.textContent = item.formatted;
          button.addEventListener('mousedown', (event) => event.preventDefault());
          button.addEventListener('click', () => selectResult(item));
          suggestions.appendChild(button);
        });
      }

      const enteredAddress = input.value.trim();
      if (enteredAddress) {
        const keepButton = document.createElement('button');
        keepButton.type = 'button';
        keepButton.className = 'repair-form__suggestion repair-form__suggestion--entered';
        keepButton.id = `${suggestions.id}-option-${items.length}`;
        keepButton.setAttribute('role', 'option');
        keepButton.setAttribute('aria-selected', 'false');
        keepButton.textContent = `Use “${enteredAddress}” as entered`;
        keepButton.addEventListener('mousedown', (event) => event.preventDefault());
        keepButton.addEventListener('click', useEnteredAddress);
        suggestions.appendChild(keepButton);
      }

      suggestions.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function updateActiveOption(nextIndex) {
      const options = suggestions.querySelectorAll('[role="option"]');
      if (!options.length) return;
      activeIndex = Math.max(0, Math.min(nextIndex, options.length - 1));
      options.forEach((option, index) => {
        const selected = index === activeIndex;
        option.setAttribute('aria-selected', String(selected));
        if (selected) {
          input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    const search = debounce(async (query) => {
      if (query.length < MIN_QUERY_LENGTH) {
        closeSuggestions();
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();

      const params = new URLSearchParams({
        text: query,
        format: 'json',
        filter: 'countrycode:us',
        lang: 'en',
        limit: '6',
        apiKey
      });

      try {
        const response = await fetch(`${API_URL}?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error(`Address search failed with ${response.status}`);
        const payload = await response.json();
        renderSuggestions(Array.isArray(payload.results) ? payload.results : []);
      } catch (error) {
        if (error.name === 'AbortError') return;
        closeSuggestions();
        if (note) note.textContent = 'Address suggestions are temporarily unavailable. You can still enter the address manually.';
      }
    }, DEBOUNCE_MS);

    input.addEventListener('input', () => {
      if (input.value !== selectedAddress) clearStructuredAddress();
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
      search(input.value.trim());
    });

    input.addEventListener('keydown', (event) => {
      if (suggestions.hidden) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        updateActiveOption(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        updateActiveOption(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        const options = suggestions.querySelectorAll('[role="option"]');
        if (options[activeIndex]) options[activeIndex].click();
      } else if (event.key === 'Escape') {
        closeSuggestions();
      }
    });

    input.addEventListener('blur', () => window.setTimeout(closeSuggestions, 150));

    form.addEventListener('repair:before-submit', () => {
      if (!fields.address.value) {
        fields.address.value = input.value.trim();
        const zipMatch = input.value.match(/\b\d{5}(?:-\d{4})?\b/);
        if (zipMatch && !fields.postcode.value) fields.postcode.value = zipMatch[0];
      }
    });
  }

  function initForm(section) {
    if (section.dataset.repairInitialized === 'true') return;
    const form = section.querySelector('[data-equipment-repair-form]');
    if (!form) return;

    section.dataset.repairInitialized = 'true';
    initializeAttribution(form);
    initializeExperiment(section, form);
    initAddressAutocomplete(section);

    const submitButton = form.querySelector('.repair-form__submit');
    const status = form.querySelector('[data-form-status]');
    const endpoint = form.dataset.endpoint;
    const formLink = section.querySelector('.equipment-repair__form-link');
    const formCard = form.closest('.equipment-repair__form-card') || form;
    const oneColumnQuery = window.matchMedia('(max-width: 989px)');
    const phone = form.querySelector('[name="phone"]');
    let submitting = false;

    if (phone) {
      phone.addEventListener('input', () => {
        updatePhoneValidity(phone);
        phone.toggleAttribute('aria-invalid', !phone.validity.valid);
      });
    }

    if (section.classList.contains('equipment-repair--equipment_repair')) {
      let stickyFrame;

      const updateStickyOffset = () => {
        stickyFrame = undefined;

        if (oneColumnQuery.matches) {
          formCard.style.removeProperty('--repair-form-sticky-top');
          return;
        }

        const rootStyles = getComputedStyle(document.documentElement);
        const rootFontSize = parseFloat(rootStyles.fontSize) || 16;
        const headerHeight = parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
        const topMargin = rootFontSize * 1.5;
        const bottomMargin = rootFontSize * 1.5;
        const standardTop = headerHeight + topMargin;
        const bottomAlignedTop = window.innerHeight - formCard.offsetHeight - bottomMargin;

        formCard.style.setProperty(
          '--repair-form-sticky-top',
          `${Math.min(standardTop, bottomAlignedTop)}px`
        );
      };

      const queueStickyOffsetUpdate = () => {
        if (stickyFrame) return;
        stickyFrame = window.requestAnimationFrame(updateStickyOffset);
      };

      updateStickyOffset();
      window.addEventListener('resize', queueStickyOffsetUpdate);

      if (window.ResizeObserver) {
        new ResizeObserver(queueStickyOffsetUpdate).observe(formCard);
      }
    }

    if (formLink) {
      let visibilityFrame;

      const updateFormLinkVisibility = () => {
        visibilityFrame = undefined;
        if (!oneColumnQuery.matches) {
          formLink.classList.remove('is-form-visible');
          return;
        }

        const linkBounds = formLink.getBoundingClientRect();
        const formBounds = formCard.getBoundingClientRect();
        const formHasReachedLink = formBounds.top <= linkBounds.bottom && formBounds.bottom >= linkBounds.top;
        formLink.classList.toggle('is-form-visible', formHasReachedLink);
      };

      const queueFormLinkVisibility = () => {
        if (visibilityFrame) return;
        visibilityFrame = window.requestAnimationFrame(updateFormLinkVisibility);
      };

      updateFormLinkVisibility();
      window.addEventListener('scroll', queueFormLinkVisibility, { passive: true });
      window.addEventListener('resize', queueFormLinkVisibility);
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submitting) return;
      form.dispatchEvent(new CustomEvent('repair:before-submit'));

      const manualAddress = form.querySelector('[data-address-input]');
      const submittedAddress = form.querySelector('[data-address-value]');
      const submittedZip = form.querySelector('[data-address-postcode]');
      if (manualAddress && submittedAddress && !submittedAddress.value) {
        submittedAddress.value = manualAddress.value.trim();
      }

      updatePhoneValidity(phone);

      if (manualAddress && submittedZip) {
        const zip = submittedZip.value.trim();
        const validZip = /^[0-9]{5}(?:-[0-9]{4})?$/u.test(zip);
        manualAddress.setCustomValidity(
          validZip ? '' : 'Select or enter a complete U.S. service address with a 5-digit ZIP code or ZIP+4.'
        );
        manualAddress.toggleAttribute('aria-invalid', !validZip);
      }

      if (!form.reportValidity()) return;

      const turnstileWidget = form.querySelector('.cf-turnstile');
      const turnstileResponse = form.querySelector('[data-turnstile-response]');

      // Read the rendered widget directly as a fallback. Turnstile can hold a
      // valid token even when its named callback was missed (for example when
      // the async API initializes before a section callback is available).
      if (turnstileWidget && turnstileResponse && !turnstileResponse.value.trim()) {
        try {
          const token = window.turnstile?.getResponse(turnstileWidget) || '';
          if (token) turnstileResponse.value = token;
        } catch (error) {
          // Let the validation message below handle an unavailable widget.
        }
      }

      if (turnstileWidget && !turnstileResponse?.value.trim()) {
        setStatus(status, 'Please complete the spam-protection check and try again.', 'error');
        return;
      }

      if (!endpoint) {
        setStatus(status, 'The form is ready, but its email endpoint has not been connected yet.', 'error');
        return;
      }

      submitting = true;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      setStatus(status, '', '');

      try {
        const payload = Object.fromEntries(new FormData(form).entries());
        const response = await fetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          const fieldErrors = result.fields && typeof result.fields === 'object'
            ? Object.values(result.fields).filter(Boolean)
            : [];
          const detail = [...new Set(fieldErrors)].join(' ');
          throw new Error(
            detail || result.error || `Submission failed with ${response.status}`
          );
        }
        publishEquipmentLead(form, result, form.querySelector('[name="form_type"]')?.value);
        form.reset();
        form.querySelectorAll('input[type="hidden"]').forEach((field) => {
          if (!['form_type', 'source_page', 'experiment_id', 'experiment_variant', 'referring_url', ...ATTRIBUTION_FIELDS].includes(field.name)) field.value = '';
        });
        if (turnstileWidget && window.turnstile) window.turnstile.reset(turnstileWidget);
        setStatus(status, form.dataset.successMessage, 'success');
      } catch (error) {
        const message = error.message && !/failed to fetch/i.test(error.message)
          ? error.message
          : 'We couldn’t send your request. Please try again or contact us directly.';
        setStatus(status, message, 'error');
      } finally {
        submitting = false;
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
    });
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-equipment-repair-section]').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
